import React, { createContext, useContext, useState, useEffect } from 'react';
import { Sparkles, Clock, Check, Search } from 'lucide-react';
import { api } from '../services/api';
import { PaymentButton } from './PaymentComponents';
import { Analytics } from '../services/analytics';
import { UserProfile } from '../services/api';
import Login from './Login';

/**
 * How long a PRO purchase lasts. Mirrors UserProfile.PRO_ACCESS_DAYS on the
 * backend, which is what actually enforces it - this is only for copy.
 */
export const PRO_ACCESS_DAYS = 14;

// PRO badge component that can be used throughout the app.
// `sm` (and `hideIcon`) exist for tight spots such as the mobile header nav.
export const ProBadge: React.FC<{
  className?: string;
  size?: 'sm' | 'md';
  hideIcon?: boolean;
}> = ({ className = '', size = 'md', hideIcon = false }) => (
  <span className={`
    inline-flex items-center rounded-full
    font-medium text-white
    bg-gradient-to-r from-blue-500 to-indigo-500
    shadow-sm
    ${size === 'sm' ? 'px-1.5 py-0.5 text-[10px] leading-none' : 'px-2.5 py-1 text-xs'}
    ${className}
  `}>
    {!hideIcon && (
      <Sparkles className={size === 'sm' ? 'w-2.5 h-2.5 mr-0.5' : 'w-3 h-3 mr-1'} />
    )}
    PRO
  </span>
);

// Context for managing subscription state
interface SubscriptionContextType {
  isProUser: boolean;
  isLoading: boolean;
  checkProStatus: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isProUser, setIsProUser] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const checkProStatus = async () => {
    try {
      const response = await api.get('/payments/check-pro-status/');
      setIsProUser(response.data.is_pro);
    } catch (error) {
      console.error('Error checking PRO status:', error);
      Analytics.logError('Subscription', 'Failed to check PRO status', 'medium');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    checkProStatus();
  }, []);

  return (
    <SubscriptionContext.Provider value={{ isProUser, isLoading, checkProStatus }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
};

interface ProtectedFeatureProps {
  children: React.ReactNode;
  userProfile: UserProfile | null;
  setUserProfile: React.Dispatch<React.SetStateAction<UserProfile | null>>;
}

export const ProtectedFeature: React.FC<ProtectedFeatureProps> = ({ 
  children, 
  userProfile, 
  setUserProfile 
}) => {
  const { isProUser, isLoading } = useSubscription();

  // What PRO actually does. Every line here has to be something the product
  // does today - we track a fixed list of guide-rated venues, refresh their
  // availability on a schedule, and link straight to the booking page. There
  // are no notifications of any kind, so nothing may promise them.
  const features = [
    'Live tables across London\'s Michelin-starred restaurants',
    'A week of availability at a glance',
    'Filter by restaurant, date and party size',
    'Book straight through on OpenTable or SevenRooms',
    'Refreshed around the clock, every few hours',
    'Includes restaurants that are hard to book direct'
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  // Check if user has access (either PRO or superuser)
  if (userProfile?.is_pro || isProUser || userProfile?.user?.is_superuser) {
    return <>{children}</>;
  }

  // Paywall component for both non-logged-in and non-pro users
  const showPaywall = () => {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Every starred table in London, on one page
          </h1>
          <p className="text-xl text-gray-600">
            We check the city's Michelin-starred restaurants through the day and
            show you what's actually free - so you stop hunting and start booking.
          </p>
        </div>
        
        {/* Video Demo Section */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 mb-8 shadow-sm">
          <div className="mb-4 text-center">
            <h2 className="text-xl md:text-2xl font-semibold text-gray-900 mb-2">
              SEE IT IN ACTION
            </h2>
            <p className="text-gray-700">
              How Eatsy PRO finds free tables at London's most booked-up restaurants
            </p>
          </div>
          
          <div className="relative w-full overflow-hidden mb-5" style={{ paddingBottom: '56.25%' }}>
            <iframe 
              className="absolute top-0 left-0 w-full h-full rounded-lg shadow-md"
              src="https://www.youtube.com/embed/kB9tgUv-tns?si=EMeM7uEjhgE64ydU" 
              title="Eatsy PRO Feature Demo" 
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" 
              referrerPolicy="strict-origin-when-cross-origin" 
              allowFullScreen
            ></iframe>
          </div>
          
          <div className="bg-white/70 rounded-lg p-4 space-y-2">
            <p className="font-medium text-gray-800">With Eatsy PRO, you can:</p>
            <ul className="list-disc pl-5 text-gray-700 space-y-1.5">
              <li>See free tables across London's Michelin-starred restaurants at once</li>
              <li>Look a week ahead and spot the nights that are still open</li>
              <li>Filter by restaurant, date and party size, then book in a click</li>
            </ul>
          </div>
        </div>

  
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          {/* Pricing Card - keeping your existing code */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="px-6 py-8">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 flex items-center justify-center">
                  Pro Plan
                  <ProBadge className="ml-2" />
                </h2>
                <div className="mt-4 flex items-baseline justify-center gap-x-2">
                  <span className="text-5xl font-bold tracking-tight text-gray-900">£8.99</span>
                  <span className="text-sm font-semibold text-gray-600">one-time payment</span>
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  Includes {PRO_ACCESS_DAYS} days of access. No subscription, nothing to cancel.
                </p>
                <p className="mt-6 text-lg text-gray-600">
                  Two weeks of the full availability list
                </p>
              </div>
              <ul className="mt-8 space-y-4">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <Check className="h-5 w-5 flex-shrink-0 text-green-500" />
                    <span className="ml-3 text-gray-600">{feature}</span>
                  </li>
                ))}
              </ul>
              {userProfile ? (
                <div className="mt-8">
                  <PaymentButton />
                </div>
              ) : (
                <div className="mt-8">
                  <div className="bg-gray-50 rounded-lg p-4 text-center mb-4">
                    <p className="text-sm text-gray-600">Sign in to unlock PRO features</p>
                  </div>
                  <Login 
                    userProfile={null}
                    setUserProfile={setUserProfile}
                  />
                </div>
              )}
            </div>
          </div>
  
          {/* Features Card - keeping your existing code */}
          <div className="bg-gray-50 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Why Choose Pro?
            </h3>
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100">
                    <Clock className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">
                    Checked around the clock
                  </h4>
                  <p className="mt-1 text-gray-600">
                    We re-check every restaurant on the list through the day, so
                    what you see is what the booking sites are showing right now.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100">
                    <Search className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">
                    One place instead of fifty tabs
                  </h4>
                  <p className="mt-1 text-gray-600">
                    Every starred restaurant in one list, filtered by date, time and
                    party size - no opening each booking page in turn to find nothing.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100">
                    <Check className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
                <div className="ml-4">
                  <h4 className="text-lg font-medium text-gray-900">
                    Straight through to booking
                  </h4>
                  <p className="mt-1 text-gray-600">
                    Every time links directly to that restaurant's booking page on
                    OpenTable or SevenRooms, with your date and party size filled in.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  

  return showPaywall();
};